import type { Point } from '../util/interpolation.js';
import {
  clamp01,
  lerp,
  snapToPixel,
} from '../util/math.js';

export interface FieldPlayer {
  role: string;
  x: number;
  y: number;
  moving: boolean;
  speedRating?: number;
  liveTempo?: number;
  down?: boolean;
  tackled?: boolean;
  [key: string]: any;
}

export interface BallPoint {
  ballX: number;
  ballY: number;
  arcadeHeight?: number;
  arcadeGroundY?: number;
}

const REGULAR_PLAYER_DRAW_WIDTH = 48;
const REGULAR_PLAYER_RIGHT_ANCHOR_X = 19;
const REGULAR_PLAYER_RIGHT_VISUAL_CENTER_X = 24;
const REGULAR_PLAYER_LEFT_ANCHOR_X = 27;
const REGULAR_PLAYER_STANDARD_LEFT_VISUAL_CENTER_X = 23;
const REGULAR_PLAYER_MIRRORED_LEFT_VISUAL_CENTER_X = REGULAR_PLAYER_DRAW_WIDTH - 1 - REGULAR_PLAYER_RIGHT_VISUAL_CENTER_X;
const REGULAR_PLAYER_RIGHT_VISIBLE_FRONT_X = 30;
const REGULAR_PLAYER_LEFT_VISIBLE_FRONT_X = 14;
const REGULAR_PLAYER_RIGHT_CARRY_HAND_X = 37;
const REGULAR_PLAYER_STANDARD_LEFT_CARRY_HAND_X = 10;
const REGULAR_PLAYER_MIRRORED_LEFT_CARRY_HAND_X = 10;
// The forward-facing center is rendered with the away-offense backset. Keep
// the snap anchor on the visible palm rather than the transparent sprite edge
// so both field directions meet the football at the line of scrimmage.
const REGULAR_PLAYER_RIGHT_SNAP_HAND_X = 42;
const REGULAR_PLAYER_MIRRORED_LEFT_SNAP_HAND_X = 5;
const REGULAR_PLAYER_OFFENSE_BACKSET_X = 10;
const REGULAR_PLAYER_AWAY_OFFENSE_EXTRA_BACKSET_X = 2;
const REGULAR_PLAYER_DEFENSE_BACKSET_X = 4;
const FOOTBALL_VISIBLE_LEFT_OFFSET = 2;
const FOOTBALL_VISIBLE_RIGHT_OFFSET = 6;
const OFFENSIVE_BACKFIELD_ROLES = new Set([
  'LT', 'LG', 'C', 'RG', 'RT',
  'QB', 'RB', 'FB', 'TE', 'WR1', 'WR2', 'WR3', 'WR4', 'SLOT',
  'HOLDER', 'KICKER', 'PUNTER', 'UPBACK',
  'BLOCK1', 'BLOCK2', 'BLOCK3', 'BLOCK4', 'BLOCK5', 'BLOCK6', 'BLOCK7',
  'PROTECT1', 'PROTECT2', 'PROTECT3', 'PROTECT4', 'PROTECT5', 'PROTECT6', 'PROTECT7',
]);

function isMirroredOffensiveRegularPlayer(teamType: string, facing: number) {
  return teamType === 'offense' && facing < 0;
}

function inferredBallCarrierTeamType(player: FieldPlayer | undefined, explicitTeamType?: string | null) {
  if (explicitTeamType === 'offense' || explicitTeamType === 'defense') {
    return explicitTeamType;
  }
  return OFFENSIVE_BACKFIELD_ROLES.has(String(player?.role || '')) ? 'offense' : 'defense';
}

export function footballVisibleFrontX(ballX: number, direction: number) {
  return snapToPixel(ballX + (direction > 0 ? FOOTBALL_VISIBLE_RIGHT_OFFSET : FOOTBALL_VISIBLE_LEFT_OFFSET));
}

export function footballVisibleBackX(ballX: number, direction: number) {
  return snapToPixel(ballX + (direction > 0 ? FOOTBALL_VISIBLE_LEFT_OFFSET : FOOTBALL_VISIBLE_RIGHT_OFFSET));
}

function ballLeftEdgeForFrontX(frontX: number, direction: number) {
  return snapToPixel(frontX - (direction > 0 ? FOOTBALL_VISIBLE_RIGHT_OFFSET : FOOTBALL_VISIBLE_LEFT_OFFSET));
}

function ballLeftEdgeForHandX(handX: number, direction: number) {
  return snapToPixel(handX - (direction > 0 ? FOOTBALL_VISIBLE_LEFT_OFFSET : FOOTBALL_VISIBLE_RIGHT_OFFSET));
}

export function regularPlayerCarryHandX(
  player: FieldPlayer,
  direction: number,
  teamType: string,
) {
  const originX = regularPlayerSpriteOriginX(player.x, direction, teamType, player.role || null, !!player.moving);
  if (direction > 0) {
    return snapToPixel(originX + REGULAR_PLAYER_RIGHT_CARRY_HAND_X);
  }
  const carryLocalX = isMirroredOffensiveRegularPlayer(teamType, direction)
    ? REGULAR_PLAYER_MIRRORED_LEFT_CARRY_HAND_X
    : REGULAR_PLAYER_STANDARD_LEFT_CARRY_HAND_X;
  return snapToPixel(originX + carryLocalX);
}

export function regularPlayerSnapHandX(
  player: FieldPlayer,
  direction: number,
  teamType: string = 'offense',
) {
  const originX = regularPlayerSpriteOriginX(player.x, direction, teamType, player.role || null, !!player.moving);
  if (direction > 0) {
    return snapToPixel(originX + REGULAR_PLAYER_RIGHT_SNAP_HAND_X);
  }
  const snapLocalX = isMirroredOffensiveRegularPlayer(teamType, direction)
    ? REGULAR_PLAYER_MIRRORED_LEFT_SNAP_HAND_X
    : REGULAR_PLAYER_LEFT_VISIBLE_FRONT_X;
  return snapToPixel(originX + snapLocalX);
}

export function regularPlayerSpriteOriginX(
  anchorX: number,
  facing: number,
  teamType: string = 'offense',
  role: string | null = null,
  moving: boolean = false,
): number {
  const offenseBaseBackset = teamType === 'offense' ? -facing * REGULAR_PLAYER_OFFENSE_BACKSET_X : 0;
  const awayOffenseExtraBackset = teamType === 'offense' && facing > 0
    ? -REGULAR_PLAYER_AWAY_OFFENSE_EXTRA_BACKSET_X
    : 0;
  const offenseBackset = offenseBaseBackset + awayOffenseExtraBackset;
  const defenseBackset = teamType === 'defense' ? -facing * REGULAR_PLAYER_DEFENSE_BACKSET_X : 0;
  const directionalBackset = offenseBackset + defenseBackset;
  if (isMirroredOffensiveRegularPlayer(teamType, facing)) {
    return snapToPixel(anchorX - REGULAR_PLAYER_LEFT_ANCHOR_X + directionalBackset);
  }
  return snapToPixel(anchorX - REGULAR_PLAYER_RIGHT_ANCHOR_X + directionalBackset);
}

export function regularPlayerVisualCenterX(
  anchorX: number,
  facing: number,
  teamType: string = 'offense',
  role: string | null = null,
  moving: boolean = false,
): number {
  const originX = regularPlayerSpriteOriginX(anchorX, facing, teamType, role, moving);
  const centerOffset = facing < 0
    ? (isMirroredOffensiveRegularPlayer(teamType, facing)
        ? REGULAR_PLAYER_MIRRORED_LEFT_VISUAL_CENTER_X
        : REGULAR_PLAYER_STANDARD_LEFT_VISUAL_CENTER_X)
    : REGULAR_PLAYER_RIGHT_VISUAL_CENTER_X;
  return snapToPixel(originX + centerOffset);
}

export function regularPlayerVisibleFrontX(
  anchorX: number,
  facing: number,
  teamType: string = 'offense',
  role: string | null = null,
  moving: boolean = false,
): number {
  const originX = regularPlayerSpriteOriginX(anchorX, facing, teamType, role, moving);
  const frontOffset = facing < 0 ? REGULAR_PLAYER_LEFT_VISIBLE_FRONT_X : REGULAR_PLAYER_RIGHT_VISIBLE_FRONT_X;
  return snapToPixel(originX + frontOffset);
}

export function createPlayer(role: string, x: number, y: number, moving: boolean = false): FieldPlayer {
  return { role, x, y, moving };
}

export function clonePlayers(players: FieldPlayer[]): FieldPlayer[] {
  return players.map((player) => ({ ...player }));
}

export function findPlayer(players: FieldPlayer[], role: string): FieldPlayer | undefined {
  return players.find((player) => player.role === role);
}

export function carriedBallPoint(
  player: FieldPlayer | undefined,
  fallbackX: number = 0,
  fallbackY: number = 0,
  direction: number = 1,
  teamType?: string | null,
): BallPoint {
  if (!player) {
    return { ballX: snapToPixel(fallbackX), ballY: snapToPixel(fallbackY) };
  }
  const resolvedTeamType = inferredBallCarrierTeamType(player, teamType);
  const handX = regularPlayerCarryHandX(player, direction, resolvedTeamType);
  const tackleDrop =
    player.tackled
    || player.down
    || player.engagementState === 'being-tackled'
    || player.engagementState === 'tackled'
      ? 4
      : 0;
  return {
    ballX: ballLeftEdgeForHandX(handX, direction),
    ballY: snapToPixel(player.y + 6 + tackleDrop),
  };
}

export function centerSnapBallPoint(center: FieldPlayer | undefined, direction: number, fallbackX: number = Number.NaN, fallbackY: number = 0): BallPoint {
  if (!center) {
    return {
      ballX: ballLeftEdgeForFrontX(Number.isFinite(fallbackX) ? fallbackX : 0, direction),
      ballY: snapToPixel(fallbackY),
    };
  }
  if (direction > 0) {
    return {
      ballX: ballLeftEdgeForFrontX(Number.isFinite(fallbackX) ? fallbackX : center.x + 12, direction),
      ballY: snapToPixel(center.y + 8),
    };
  }
  const originX = regularPlayerSpriteOriginX(center.x, direction, 'offense', center.role || 'C', false);
  return {
    ballX: ballLeftEdgeForFrontX(
      Number.isFinite(fallbackX) ? fallbackX : originX + REGULAR_PLAYER_MIRRORED_LEFT_SNAP_HAND_X,
      direction,
    ),
    ballY: snapToPixel(center.y + 8),
  };
}

export function puntSnapBallPoint(snapper: FieldPlayer | undefined, direction: number, fallbackX: number = Number.NaN, fallbackY: number = 0): BallPoint {
  if (!snapper) {
    return {
      ballX: ballLeftEdgeForFrontX(Number.isFinite(fallbackX) ? fallbackX : 0, direction),
      ballY: snapToPixel(fallbackY),
    };
  }
  return {
    ballX: ballLeftEdgeForFrontX(
      Number.isFinite(fallbackX) ? fallbackX : snapper.x + (direction > 0 ? 12 : -12),
      direction,
    ),
    ballY: snapToPixel(snapper.y + 8),
  };
}

export function fieldGoalSnapBallPoint(snapper: FieldPlayer | undefined, direction: number, fallbackX: number = Number.NaN, fallbackY: number = 0): BallPoint {
  if (!snapper) {
    return {
      ballX: ballLeftEdgeForFrontX(Number.isFinite(fallbackX) ? fallbackX : 0, direction),
      ballY: snapToPixel(fallbackY),
    };
  }
  return {
    ballX: ballLeftEdgeForFrontX(
      Number.isFinite(fallbackX) ? fallbackX : snapper.x + (direction > 0 ? 20 : -20),
      direction,
    ),
    ballY: snapToPixel(snapper.y + 8),
  };
}

export function puntCarryBallPoint(player: FieldPlayer | undefined, direction: number, fallbackX: number = 0, fallbackY: number = 0): BallPoint {
  if (!player) {
    return { ballX: snapToPixel(fallbackX), ballY: snapToPixel(fallbackY) };
  }
  return carriedBallPoint(player, fallbackX, fallbackY, direction, 'offense');
}

export function scrimmageBallPoint(offensePlayers: FieldPlayer[], direction: number, fallbackX: number = 0, fallbackY: number = 0): BallPoint {
  return centerSnapBallPoint(findPlayer(offensePlayers, 'C'), direction, fallbackX, fallbackY);
}

export function blendBallPoint(start: BallPoint, end: BallPoint, amount: number): BallPoint {
  return {
    ballX: snapToPixel(lerp(start.ballX, end.ballX, clamp01(amount))),
    ballY: snapToPixel(lerp(start.ballY, end.ballY, clamp01(amount))),
  };
}

export function ballAnchorPointsForPlayer(player: FieldPlayer, frame: any, teamType: string = 'offense'): Point[] {
  const carryDirection = frame?.direction || 1;
  const carryPoint = carriedBallPoint(player, player.x, player.y, carryDirection, teamType);
  const anchors: Point[] = [{
    x: carryPoint.ballX,
    y: carryPoint.ballY,
  }];
  if (teamType === 'offense' && player.role === 'C') {
    anchors.push({ x: regularPlayerSnapHandX(player, carryDirection, teamType), y: snapToPixel(player.y + 8) });
  }
  if (player.role === 'HOLDER') {
    anchors.push({ x: snapToPixel(player.x + 10), y: snapToPixel(player.y + 8) });
  }
  return anchors;
}

export function setPlayerState(players: FieldPlayer[], role: string, newState: { x: number; y: number; moving?: boolean }): FieldPlayer | null {
  const player = findPlayer(players, role);
  if (!player) return null;
  player.x = snapToPixel(newState.x);
  player.y = snapToPixel(newState.y);
  player.moving = !!newState.moving;
  return player;
}

export function setPlayersStill(...playerGroups: FieldPlayer[][]): void {
  playerGroups.flat().forEach((player) => { player.moving = false; });
}

export function nearestPlayerToPoint(players: FieldPlayer[], target: Point, allowedRoles: string[] | null = null): FieldPlayer | null {
  let best: FieldPlayer | null = null;
  let bestDist = Infinity;
  for (const player of players) {
    if (allowedRoles && !allowedRoles.includes(player.role)) continue;
    const dist = Math.hypot((player.x || 0) - (target.x || 0), (player.y || 0) - (target.y || 0));
    if (dist < bestDist) {
      bestDist = dist;
      best = player;
    }
  }
  return best;
}
